import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';

import {
  ActionBar,
  ChakraFlex,
  ChakraHStack,
  DatePickerInput,
  FAIconName,
  FireflyButton,
  InputGroupV2,
  ModalV2,
  UniversalSelect,
} from '@equip.health/ui';

import { dropdownMenuOptionsMapper } from '~/lib/util/inquiry.util';
import { TreatmentEpisodeProgramDetails } from './Types';

const TOP_LEVEL_ID = 'discharge-patient-modal';
const DISCHARGE_DATE_ID = `${TOP_LEVEL_ID}__discharge-date`;
const DISCHARGE_REASON_ID = `${TOP_LEVEL_ID}__discharge-reason`;

export interface DischargePatientModalProps {
  admitDate?: Date;
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: (reason: EpisodeStatusReason, date: Date) => void;
  statusReasons: EpisodeStatusReason[];
  programs?: TreatmentEpisodeProgramDetails[];
}

const DischargePatientModal = ({
  admitDate,
  isOpen,
  onCancel,
  onConfirm,
  statusReasons,
  programs,
}: DischargePatientModalProps) => {
  const [statusReason, setStatusReason] = useState<string>();
  const [date, setDate] = useState<Date>();

  const dischargeStatuses = useMemo(
    () =>
      (statusReasons || [])
        .filter((x) => x.status === 'DISCHARGED')
        .map((x) => x.label),
    [statusReasons],
  );

  const options = useMemo(
    () => dropdownMenuOptionsMapper(dischargeStatuses),
    [dischargeStatuses],
  );

  const option = useMemo(
    () => (options || []).find((x) => x.label === statusReason),
    [options, statusReason],
  );

  const handleConfirm = () => {
    onConfirm(
      statusReasons.find((x) => x.label === statusReason),
      date,
    );
  };

  const showDischargeWarning =
    date &&
    programs?.some(
      (program) =>
        program.startDate &&
        DateTime.fromISO(program.startDate).toISODate() >=
          DateTime.fromJSDate(date).toISODate(),
    );

  return (
    <ModalV2
      footerContent={
        <ChakraHStack justifyContent="space-between" width="100%">
          <FireflyButton
            id={`${TOP_LEVEL_ID}__cancel`}
            leftIcon={FAIconName.timesRegular}
            onClick={onCancel}
            variant="secondary"
          >
            Cancel
          </FireflyButton>
          <FireflyButton
            id={`${TOP_LEVEL_ID}__discharge`}
            isDisabled={!statusReason || !date}
            leftIcon={FAIconName.checkRegular}
            onClick={handleConfirm}
          >
            Discharge patient
          </FireflyButton>
        </ChakraHStack>
      }
      id={`${TOP_LEVEL_ID}__modal`}
      isOpen={isOpen}
      onClose={onCancel}
      title="Discharge patient"
    >
      <ChakraFlex direction="column" gap="24px" padding="1px" width="100%">
        <InputGroupV2 id={DISCHARGE_DATE_ID} label="Discharge date">
          <DatePickerInput
            defaultDate={date}
            id={DISCHARGE_DATE_ID}
            minDate={
              admitDate
                ? DateTime.fromJSDate(admitDate).plus({ days: 1 }).toJSDate()
                : undefined
            }
            onSubmit={setDate}
            placeholder="Please choose"
            showTimeInput={false}
          />
        </InputGroupV2>
        <InputGroupV2 id={DISCHARGE_REASON_ID} label="Discharge reason">
          <UniversalSelect
            id={DISCHARGE_REASON_ID}
            isSingleSelect
            modalId={TOP_LEVEL_ID}
            onChange={(v) => setStatusReason(v?.value)}
            options={options}
            value={option}
          />
        </InputGroupV2>
        {showDischargeWarning && (
          <ActionBar
            barIcon={FAIconName.exclamationTriangleRegular}
            id="treatment-episode--discharge-warning"
            text="Are you sure you want to continue?
We detected a treatment program that starts after the discharge date. Continuing to discharge the patient will delete the future treatment program. If you don’t want this to happen, please select a future discharge date."
            variant="critical"
          />
        )}
      </ChakraFlex>
    </ModalV2>
  );
};

export default DischargePatientModal;
